import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { App, Card, Descriptions, Table, Button, Spin, Result, Space, Tag, Image } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { formatCurrency, formatDate } from '../utils/format';

interface ProductVariant {
  id: string;
  title: string;
  sku?: string;
  barcode?: string;
  prices?: Array<{ id: string; amount: number; currency_code: string }>;
}

interface ProductDetailData {
  id: string;
  title: string;
  status: string;
  description?: string;
  thumbnail?: string;
  type?: { id: string; value: string };
  collection?: { id: string; title: string };
  variants?: ProductVariant[];
  options?: Array<{ id: string; title: string; values: Array<string | { id?: string; value?: string }> }>;
  images?: Array<{ id: string; url: string }>;
  created_at: string;
  updated_at: string;
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [product, setProduct] = useState<ProductDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/products/${id}`);
      setProduct(response.data.product || response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error(error.response?.data?.message || t('common.noData'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <Result
        status="404"
        title="404"
        subTitle={t('common.noData')}
        extra={
          <Button type="primary" onClick={() => navigate('/products')}>
            {t('common.back')}
          </Button>
        }
      />
    );
  }

  const variantColumns = [
    {
      title: t('products.name'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('products.sku'),
      dataIndex: 'sku',
      key: 'sku',
      render: (sku: string) => sku || '-',
    },
    {
      title: t('products.price'),
      key: 'price',
      render: (_: any, record: ProductVariant) => {
        const price = record.prices?.[0];
        if (!price) return '-';
        return formatCurrency(price.amount, price.currency_code, i18n.language);
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/products')}>
          {t('common.back')}
        </Button>
      </Space>

      <Card title={t('products.title')} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 24 }}>
          {product.thumbnail && (
            <Image
              src={product.thumbnail}
              alt={product.title}
              width={120}
              height={120}
              style={{ objectFit: 'cover', borderRadius: 8 }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
            />
          )}
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} style={{ flex: 1 }}>
            <Descriptions.Item label={t('products.name')}>{product.title}</Descriptions.Item>
            <Descriptions.Item label={t('products.status')}>
              <Tag color={product.status === 'published' ? 'green' : 'default'}>
                {product.status === 'published' ? t('products.published') : t('products.draft')}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('products.type')}>{product.type?.value || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('products.collection')}>{product.collection?.title || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {formatDate(product.created_at, i18n.language)}
            </Descriptions.Item>
          </Descriptions>
        </div>
        {product.description && (
          <div style={{ marginTop: 16 }}>
            <strong>{t('common.description')}:</strong>
            <p style={{ marginTop: 8 }}>{product.description}</p>
          </div>
        )}
      </Card>

      {product.options && product.options.length > 0 && (
        <Card title={t('products.options')} style={{ marginBottom: 16 }}>
          <Descriptions column={1}>
            {product.options.map((opt) => (
              <Descriptions.Item key={opt.id} label={opt.title}>
                {opt.values?.map((v) => typeof v === 'string' ? v : v.value || String(v)).join(', ') || '-'}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>
      )}

      <Card title={t('products.variants')}>
        <Table
          columns={variantColumns}
          dataSource={product.variants || []}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('common.noData') }}
        />
      </Card>
    </div>
  );
};

export default ProductDetail;
